import 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import { TriggerController } from 'Components/controllers/trigger-controller';
import 'Components/molecules/vm-modal/vm-modal';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import Styles from './vm-content-popper.scss';

/**
 * Responsive container that renders its content in a `vm-modal` on desktop
 * and a `vm-bottom-sheet` on mobile (below 1024px). Forwards `open`, `disable-backdrop` and all
 * three slots through to whichever child is active.
 *
 * @slot - Default slot for the body content.
 * @slot header - Header content, shown next to the child's close button.
 * @slot footer - Optional action row pinned below the body.
 *
 * @fires vm-close - Fired when the user dismisses the popper.
 *
 * @example
 * ```html
 * <button id="info">More info</button>
 *
 * <vm-content-popper trigger="#info">
 *   <span slot="header">Title</span>
 *   <p>Content</p>
 *   <div slot="footer"><button>Save</button></div>
 * </vm-content-popper>
 * ```
 */
@customElement('vm-content-popper')
export class VmContentPopper extends LitElement {
    static override styles = Styles;

    /** Whether the popper is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** Hides the backdrop and disables close-on-outside-click. */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop' })
    disableBackdrop = false;

    /**
     * CSS selector for an external element that opens the popper on click.
     * The element must exist in the DOM when the popper connects — binding
     * is one-shot, not observed.
     */
    @property()
    trigger?: string;

    private _mql = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);

    @state()
    private _isDesktop = this._mql.matches;

    private _triggerCtrl = new TriggerController(this, {
        getSelector: () => this.trigger,
        onActivate: () => {
            this.open = true;
        },
    });

    private _onBreakpointChange = (e: MediaQueryListEvent) => {
        // Just flip _isDesktop — Lit re-renders, swapping vm-modal ↔ vm-bottom-sheet
        // in place. The new wrapper mounts already-open, so the user keeps their
        // place across the breakpoint crossing.
        this._isDesktop = e.matches;
    };

    // The popper is the single source of vm-close — stop the child's
    // event so we don't double-fire when updated() re-dispatches.
    private _onChildClose = (e: Event) => {
        e.stopPropagation();
        this.open = false;
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this._mql.addEventListener('change', this._onBreakpointChange);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._mql.removeEventListener('change', this._onBreakpointChange);
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has('open')) {
            this._triggerCtrl.setExpanded(this.open);
            if (!this.open && changed.get('open') === true) {
                this.dispatchEvent(new CustomEvent('vm-close', { bubbles: true, composed: true }));
                // Restore focus to the trigger so keyboard/SR users don't lose their place.
                this._triggerCtrl.focus();
            }
        }
    }

    override render() {
        if (!this.open) return nothing;

        if (this._isDesktop) {
            return html`
                <vm-modal open ?disable-backdrop=${this.disableBackdrop} @vm-close=${this._onChildClose}>
                    <slot name="header" slot="header"></slot>
                    <slot></slot>
                    <slot name="footer" slot="footer"></slot>
                </vm-modal>
            `;
        }

        return html`
            <vm-bottom-sheet open ?disable-backdrop=${this.disableBackdrop} @vm-close=${this._onChildClose}>
                <slot name="header" slot="header"></slot>
                <slot></slot>
                <slot name="footer" slot="footer"></slot>
            </vm-bottom-sheet>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-content-popper': VmContentPopper;
    }
}
